import type { OfferCargoProcess } from "@/features/offer-cargo-process-status";
import {
  calculateLatestOnGoingDeliveryProposalOfferCargo,
  calculateLatestValidContractOfferCargo,
} from "@/features/offer-cargo-process-status";
import { formatMoney } from "@/shared/lib/currency";
import { Box, Stack, Typography } from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import type React from "react";

const LabelConfings = {
  draft: {
    shipper: "希望運賃",
    carrier: "荷主希望",
    color: "#52A1D6",
  },
  deliveryProposal: {
    shipper: "車主提案",
    carrier: "提案運賃",
    color: "#3666E0",
  },
  contract: {
    shipper: "契約運賃",
    carrier: "契約運賃",
    color: "#9973E5",
  },
} as const;

const Badge = (props: {
  viewer: "shipper" | "carrier";
  labelConfig: {
    shipper: string;
    carrier: string;
    color: string;
  };
}) => {
  const { viewer, labelConfig } = props;
  return (
    <Box
      bgcolor={labelConfig.color}
      color="white"
      width="48px"
      height="16px"
      borderRadius={1}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Typography fontSize="10px" fontWeight={700}>
        {labelConfig[viewer]}
      </Typography>
    </Box>
  );
};

const DiffMessage = (props: {
  viewer: "shipper" | "carrier";
  emphasis: boolean;
  diff: number;
}) => {
  const { viewer, emphasis, diff } = props;
  const baseLabel = viewer === "shipper" ? "希望運賃" : "荷主希望";
  const color = emphasis && diff > 0 ? "#EA583B" : "grey.500";
  return (
    <Typography variant="body1" color={color}>
      {diff >= 0
        ? `(${baseLabel}より+${formatMoney(diff)})`
        : `(${baseLabel}より${formatMoney(diff)})`}
    </Typography>
  );
};

type ShippingFeeRowProps = {
  viewer: "shipper" | "carrier";
  emphasis: boolean;
  labelConfig: {
    shipper: string;
    carrier: string;
    color: string;
  };
  shippingFee: number | string;
  diff: number | undefined;
};
const ShippingFeeRow = (props: ShippingFeeRowProps) => {
  const { viewer, emphasis, labelConfig, shippingFee } = props;
  return (
    <Stack direction="row" alignItems="center" spacing={2} height={40}>
      <Badge viewer={viewer} labelConfig={labelConfig} />
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography
          variant={
            typeof shippingFee === "number"
              ? emphasis
                ? "h4"
                : "h5"
              : emphasis
                ? "h5"
                : "h6"
          }
          color={emphasis ? "grey.900" : "grey.500"}
          fontWeight={emphasis ? 700 : undefined}
        >
          {typeof shippingFee === "number"
            ? formatMoney(shippingFee)
            : shippingFee}
        </Typography>
        {props.diff !== undefined ? (
          <DiffMessage {...props} diff={props.diff} />
        ) : null}
      </Stack>
    </Stack>
  );
};

export type OfferCargoShippingFeeInfoProps = {
  viewer: "shipper" | "carrier";
  offerCargoProcess: OfferCargoProcess;
  deliveryProposalOfferCargoId: number | undefined;
};

export const OfferCargoShippingFeeInfo: React.FC<
  OfferCargoShippingFeeInfoProps
> = ({ viewer, offerCargoProcess, deliveryProposalOfferCargoId }) => {
  // 荷物のドラフト情報を取得
  const draftOfferCargo = offerCargoProcess.draftOfferCargo;
  const draftFee = draftOfferCargo.shippingFee;

  // 配送提案情報を取得
  let deliveryProposalOfferCargo:
    | FreightMatching.schemas.OfferCargo
    | undefined;
  if (deliveryProposalOfferCargoId) {
    // 指定されたIDの配送提案を取得
    deliveryProposalOfferCargo =
      offerCargoProcess.deliveryProposalOfferCargos.find(
        (cargo) => cargo.id === deliveryProposalOfferCargoId,
      );
  } else {
    // 最新の進行中の配送提案を取得
    deliveryProposalOfferCargo =
      calculateLatestOnGoingDeliveryProposalOfferCargo({
        offerCargoProcess,
      });
  }

  // 契約情報を取得
  let contractOfferCargo: FreightMatching.schemas.OfferCargo | undefined;
  if (deliveryProposalOfferCargo) {
    // 配送提案に関連する最新の有効な契約を取得
    contractOfferCargo = calculateLatestValidContractOfferCargo({
      deliveryProposalOfferCargoId: deliveryProposalOfferCargo.id,
      offerCargoProcess,
    });
  }

  // 強調表示の条件を決定
  const hasContract = !!contractOfferCargo;
  const hasDeliveryProposal = !!deliveryProposalOfferCargo;

  // draftのshippingFeeが0の場合、荷主からの希望運賃がないものとみなす
  const noShipperShippingFeeRequest = draftFee === 0;

  // ドラフト情報
  const draftShippingFee: ShippingFeeRowProps = {
    viewer,
    emphasis: !hasContract && !hasDeliveryProposal,
    labelConfig: LabelConfings.draft,
    shippingFee: (() => {
      if (noShipperShippingFeeRequest) {
        if (viewer === "shipper") {
          return "車主から提案";
        }
        return "提案してください";
      }
      return draftFee;
    })(),
    diff: undefined, // ドラフトには差分は表示しない
  };

  // 配送提案情報
  let deliveryProposalShippingFee: ShippingFeeRowProps | undefined;
  if (hasDeliveryProposal && deliveryProposalOfferCargo) {
    const proposalFee = deliveryProposalOfferCargo.shippingFee;
    deliveryProposalShippingFee = {
      viewer,
      emphasis: !hasContract, // 契約がない場合のみ強調表示
      labelConfig: LabelConfings.deliveryProposal,
      shippingFee: proposalFee,
      diff: noShipperShippingFeeRequest ? undefined : proposalFee - draftFee,
    };
  }

  // 契約情報
  let contractShippingFee: ShippingFeeRowProps | undefined;
  if (hasContract && contractOfferCargo) {
    const contractFee = contractOfferCargo.shippingFee;
    contractShippingFee = {
      viewer,
      emphasis: true, // 契約は常に強調表示
      labelConfig: LabelConfings.contract,
      shippingFee: contractFee,
      diff: noShipperShippingFeeRequest ? undefined : contractFee - draftFee,
    };
  }

  return (
    <>
      <Typography
        variant="body1"
        fontWeight={700}
        mb={1}
        sx={{
          "::before": {
            content: '"■"',
            fontSize: "150%",
            marginRight: "2px",
          },
        }}
      >
        運賃
      </Typography>
      <Stack
        width={628}
        boxShadow="0px 1px 10px 0px #0000001A"
        p={3}
        borderRadius={4}
      >
        {contractShippingFee ? (
          <ShippingFeeRow
            key={contractShippingFee.labelConfig[viewer]}
            {...contractShippingFee}
            viewer={viewer}
          />
        ) : null}
        {deliveryProposalShippingFee ? (
          <ShippingFeeRow
            key={deliveryProposalShippingFee.labelConfig[viewer]}
            {...deliveryProposalShippingFee}
            viewer={viewer}
          />
        ) : null}
        {draftShippingFee ? (
          <ShippingFeeRow
            key={draftShippingFee.labelConfig[viewer]}
            {...draftShippingFee}
            viewer={viewer}
          />
        ) : null}
      </Stack>
    </>
  );
};
